import { NavLink } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * A single entry in a <ControlBar>. Either a router link (`to` → NavLink with
 * URL-derived active styling) or an action button (`onClick` → ghost button).
 */
export interface ControlBarItem {
  icon: LucideIcon;
  label: string;
  /** Render as a router NavLink (active state derives from the URL). */
  to?: string;
  /** Render as a ghost action button. */
  onClick?: () => void;
  /** Optional forced-active flag (mainly for button items / tests). */
  active?: boolean;
}

interface ControlBarProps {
  /** Bold section label shown at the left of the bar. */
  label: string;
  items: ControlBarItem[];
  /**
   * Tailwind classes for the bar wrapper's background + text accent
   * (e.g. AdminBar's indigo gradient, EnterpriseLayout's sky fill).
   */
  accentClassName?: string;
}

/**
 * Presentational thin section bar — a bold label + a row of items. Each item is
 * either a NavLink (`to`) for URL-addressable section sub-nav (active styling
 * derives from the route) or a ghost button (`onClick`) for actions (e.g.
 * AdminBar's panel Sheets). No data fetching / gating lives here.
 */
export function ControlBar({
  label,
  items,
  accentClassName = 'bg-muted text-foreground',
}: ControlBarProps): JSX.Element {
  return (
    <div
      className={cn(
        'h-9 flex items-center px-4 text-sm shrink-0',
        accentClassName,
      )}
    >
      <span className="font-semibold mr-6">{label}</span>
      <div className="flex items-center gap-1">
        {items.map((item) => {
          const Icon = item.icon;
          if (item.to) {
            return (
              <NavLink
                key={item.label}
                to={item.to}
                end
                className={({ isActive }) =>
                  cn(
                    'inline-flex items-center h-7 px-3 rounded-md font-medium transition-colors',
                    isActive || item.active
                      ? 'bg-white/20 text-white'
                      : 'text-white/90 hover:text-white hover:bg-white/10',
                  )
                }
              >
                <Icon className="h-3.5 w-3.5 mr-1.5" />
                {item.label}
              </NavLink>
            );
          }
          return (
            <Button
              key={item.label}
              variant="ghost"
              size="sm"
              className={cn(
                'h-7 px-3 text-white/90 hover:text-white hover:bg-white/10',
                item.active && 'bg-white/20 text-white',
              )}
              onClick={item.onClick}
            >
              <Icon className="h-3.5 w-3.5 mr-1.5" />
              {item.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
